import { ChevronRightIcon, HistoryIcon } from 'components/icons';
import { usePostHog } from '@posthog/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Skeleton } from 'components/ui/skeleton';
import { FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { useGetAiChatThreadsQuery } from 'store/api/aiChatApi';
import { cn } from 'utils/cn';
import { familyTileClass } from 'utils/families';
import type { AiChatSurface } from '../hooks/useAiChatController';
import { formatRelativeTimeFromNow } from '../utils/relativeTime';

interface AiChatResumeCardProps {
    surface: AiChatSurface;
    onSelectThread: (threadId: number) => void;
}

/**
 * "Continue previous chat" offer shown in a surface's empty state when a fresh chat opened instead
 * of resuming. Reads the most recent thread from the threads list (already sorted by last message)
 * rather than from the resume record, so it is accurate on a fresh device and after deleting the
 * current thread. Renders nothing when there is no previous chat, and a placeholder of its own height
 * while the threads load, so what sits under it does not drop when it lands.
 */
const AiChatResumeCard: React.FC<AiChatResumeCardProps> = ({ surface, onSelectThread }) => {
    const intl = useIntl();
    const posthog = usePostHog();
    const { data: threads, isLoading } = useGetAiChatThreadsQuery(0);
    const latestThread = threads?.[0];

    if (isLoading) {
        return <Skeleton className="h-14 w-full rounded-[20px]" />;
    }

    if (!latestThread) {
        return null;
    }

    const title = latestThread.title || intl.formatMessage({ id: 'ai-chat.untitled', defaultMessage: 'Untitled Chat' });
    const lastMessageAtMs = Date.parse(latestThread.lastMessageAt);

    return (
        <button
            type="button"
            onClick={() => {
                posthog.capture('ai_chat_resume_card_clicked', { surface });
                onSelectThread(latestThread.id);
            }}
            className={cn(
                'group/row border-border bg-card hover:bg-row-stripe new:border-0 new:shadow-card new:not-focus-visible:outline-hidden flex min-h-14 w-full cursor-pointer items-center gap-3 rounded-[20px] border px-3 py-2.5 text-start transition-colors',
                FOCUS_RING
            )}
        >
            {/* The card's own tile, the same for every chat: the history rows' tiles stay neutral. */}
            <span className={cn('grid size-9 shrink-0 place-items-center rounded-[10px]', familyTileClass('finance'))}>
                <HistoryIcon className="size-[18px]" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-caption text-foreground-tertiary">
                    <FormattedMessage id="ai-chat.resume.card-title" />
                    {Number.isFinite(lastMessageAtMs) ? ` · ${formatRelativeTimeFromNow(intl, lastMessageAtMs)}` : null}
                </span>
                <span className="text-meta text-foreground font-semibold text-pretty">{title}</span>
            </span>
            {/* At 70% at rest, the arrow keeps 3:1 on every surface; the row's hover brings it to full. */}
            <ChevronRightIcon className="text-foreground-tertiary size-4 shrink-0 opacity-70 transition-opacity group-hover/row:opacity-100 rtl:-scale-x-100" />
        </button>
    );
};

export default AiChatResumeCard;
